import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';
import { TabService } from '@core/services/tab.service';

@Injectable({ providedIn: 'root' })
export class LayoutRouteReuseStrategy implements RouteReuseStrategy {
  private readonly tabs = inject(TabService);

  /**
   * Component-less grouping routes (parents with only `children`) must never be detached,
   * stored, or attached. They share the resolved `fullRoute` key with their matched child,
   * and reusing a handle for both nodes makes the router state tree cyclic, overflowing
   * `setRouterState`.
   */
  private isComponentRoute(route: ActivatedRouteSnapshot): boolean {
    const cfg = route.routeConfig;
    return !!(route.component || cfg?.component || cfg?.loadComponent);
  }

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    const detach = this.isComponentRoute(route);
    // The outgoing component is detached (not destroyed), so its open PrimeNG overlays
    // (appendTo="body") never run their close animation and are left dangling in <body>.
    // Hiding them synchronously here runs before Angular freezes change detection for the view.
    if (detach) this.hideOpenOverlays();
    return detach;
  }

  private hideOpenOverlays(): void {
    //todo:tab değiştirince açık kalan popup lar i.in çözüm yolu bence çok doğru bir çözüm yolu değil .değerlendirelebilir
    // Menu/TieredMenu wrap their root in <p-motion>, so they aren't a direct child of <body>.
    const overlays = document.querySelectorAll<HTMLElement>(
      'body > .p-overlay:not(.p-overlay-modal), body .p-menu-overlay, body .p-tieredmenu-overlay, body .p-contextmenu, body .p-popover, body > .p-tooltip, body > .p-confirmpopup'
    );
    if (overlays.length === 0) return;
    // display:none alone leaves PrimeNG's internal "visible" state stuck true, so the next
    // show() just repositions the hidden element instead of reopening it. A real outside
    // click first lets each overlay close itself properly via its own listener.
    document.body.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
    overlays.forEach((el) => {
      el.style.display = 'none';
    });
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    if (!this.isComponentRoute(route)) return;
    const key = route.data['fullRoute'] as string | undefined;
    if (!key) return;
    if (this.tabs.consumeSuppressStore(key)) return;
    // "Add" forms all resolve to the same URL for a given type (no id to make it unique), so
    // caching the handle risks reattaching a stale, possibly half-initialized instance the next
    // time the same add-form is opened. That's only a real risk once this tab has actually been
    // closed (save/cancel closes it via `closeTab`, which also clears `suppressStoreKeys` above) —
    // while the tab is still open, this is just the user switching to another tab and back, and
    // their in-progress entry should survive that like any other form.
    if (route.queryParams['mode'] === 'add' && !this.tabs.hasTab(key)) return;
    if (handle && !key.startsWith('/auth')) {
      this.tabs.setHandle(key, handle);
    }
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    if (!this.isComponentRoute(route)) return false;
    const key = route.data['fullRoute'] as string | undefined;
    return !!key && this.tabs.hasHandle(key);
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    if (!this.isComponentRoute(route)) return null;
    const key = route.data['fullRoute'] as string | undefined;
    return key ? (this.tabs.getHandle(key) ?? null) : null;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    if (future.routeConfig !== curr.routeConfig) return false;
    return future.data['fullRoute'] === curr.data['fullRoute'];
  }
}
